// src/components/programs/ProgramForm/helpers/quotationCalculations.js
import { PRICING_CODES } from "./formConstants";

/**
 * Calculate quantity based on pricing unit and dynamic fields
 */
export const calculateQuantity = (product, paxTypes) => {
    const pricingCode = product.product_snapshot?.pricing_unit;
    const totalPax = Array.isArray(paxTypes)
        ? paxTypes.reduce((sum, pt) => sum + (pt.count || 0), 0)
        : (typeof paxTypes === 'number' ? paxTypes : 0);

    const nights = product.dynamic_fields?.nights || 0;
    
    const typeGroup = getProductTypeGroup(product);
    const isFlight = typeGroup === 'flight';
    const repetitions = isFlight ? 1 : (product.repetition_count || 1);

    if (product.merged_quantities) {
        if (pricingCode === "per_person") {
            return repetitions * totalPax;
        } else if (pricingCode === "per_night") {
            return product.merged_quantities.nights;
        } else if (pricingCode === "per_unit") {
            return product.merged_quantities.units;
        } else if (pricingCode === "per_person_night") {
            return totalPax * nights * repetitions;
        }
    }

    if (pricingCode === "per_person") {
        return totalPax;
    } else if (pricingCode === "per_night") {
        return product.dynamic_fields?.nights || 0;
    } else if (pricingCode === "per_unit") {
        return product.dynamic_fields?.units || 0;
    } else if (pricingCode === "per_person_night") {
        return totalPax * nights;
    }

    return 0;
};

/**
 * Calculate product final price after discount
 */
export const calculateProductFinalPrice = (basePrice, product) => {
    const discountType = product.pricing?.discount_type || "zero";
    const discountValue = product.pricing?.discount_value || 0;

    if (discountType === "zero" || !discountValue) {
        return basePrice;
    }

    if (discountType === "amount") {
        return Math.max(0, basePrice - discountValue);
    }

    if (discountType === "percent") {
        return basePrice - (basePrice * discountValue / 100);
    }

    return basePrice;
};

/**
 * Calculate product totals (price and cost) for itinerary products
 */
export const calculateProductTotals = (product, paxTypes) => {
    const quantity = calculateQuantity(product, paxTypes);
    const pricingCode = product.product_snapshot?.pricing_unit;
    const isPerPerson = pricingCode === "per_person";
    const isPerPersonNight = pricingCode === "per_person_night";
    const paxTypesArray = Array.isArray(paxTypes) ? paxTypes : [];

    const typeGroup = getProductTypeGroup(product);
    const isFlight = typeGroup === 'flight';
    const repetitions = isFlight ? 1 : (product.repetition_count || 1);

    let totalPrice = 0;
    let totalCost = 0;
    let avgPricePerUnit = 0;
    let avgCostPerUnit = 0;

    const typeCode = product.product_snapshot?.productType?.code || product.product_snapshot?.productType ||
        product.product_snapshot?.product_type?.code || product.product_snapshot?.product_type ||
        product.productType?.code || product.productType ||
        product.product_type?.code || product.product_type;
        
    const isNonPriced = pricingCode === PRICING_CODES.FREE_DAY || 
                        pricingCode === PRICING_CODES.FREE_TIME || 
                        pricingCode === PRICING_CODES.PROGRAM_MARKER ||
                        typeCode === 'free_day' || 
                        typeCode === 'free_time';

    if (isNonPriced) {
        return {
            quantity: 0,
            final_price_per_unit: 0,
            final_cost_per_unit: 0,
            total_price: 0,
            total_cost: 0,
        };
    }

    const isPriceEdited = product.pricing?.is_price_edited || false;
    const customPrice = parseFloat(product.pricing?.custom_price);
    const hasGlobalCustomPrice = isPriceEdited && !isNaN(customPrice) && customPrice >= 0;

    const paxPrices = product.product_snapshot?.pax_type_prices || product.pax_type_prices || [];
    const hasPaxPrices = (isPerPerson || isPerPersonNight) && paxPrices.length > 0;

    if ((isPerPerson || isPerPersonNight) && hasPaxPrices) {
        const nights = isPerPersonNight ? (product.dynamic_fields?.nights || 0) : 1;
        let actualItemsCalculated = 0;

        paxTypesArray.forEach(pt => {
            const count = pt.count || 0;
            if (count > 0) {
                const paxConfig = paxPrices.find(p => String(p.pax_type_id) === String(pt.pax_type_id));

                const customPaxPrice = parseFloat(product.pricing?.custom_pax_prices?.[pt.pax_type_id]);
                const hasCustomPaxPrice = isPriceEdited && !isNaN(customPaxPrice) && customPaxPrice >= 0;

                const paxPriceVal = parseFloat(paxConfig?.price);
                const basePrice = hasCustomPaxPrice
                    ? customPaxPrice
                    : (isNaN(paxPriceVal) ? 0 : paxPriceVal);

                const customPaxCost = product.pricing?.custom_pax_costs?.[pt.pax_type_id];
                const hasCustomPaxCost = customPaxCost !== undefined && customPaxCost !== null && !isNaN(parseFloat(customPaxCost));

                if (!paxConfig && !hasCustomPaxPrice && !hasCustomPaxCost) { return; }

                const paxCostVal = parseFloat(paxConfig?.cost_price);
                const defaultCost = isNaN(paxCostVal) ? (parseFloat(product.pricing?.cost_price) || 0) : paxCostVal;
                const baseCost = hasCustomPaxCost
                    ? parseFloat(customPaxCost)
                    : defaultCost;

                const finalPrice = calculateProductFinalPrice(basePrice, product);

                totalPrice += finalPrice * count * nights * repetitions;
                totalCost += baseCost * count * nights * repetitions;
                actualItemsCalculated += count * repetitions;
            }
        });

        avgPricePerUnit = actualItemsCalculated > 0 ? totalPrice / actualItemsCalculated : 0;
        avgCostPerUnit = actualItemsCalculated > 0 ? totalCost / actualItemsCalculated : 0;
    } else {
        const basePrice = hasGlobalCustomPrice ? customPrice : (parseFloat(product.pricing?.price) || 0);

        const customCostPriceVal = parseFloat(product.pricing?.custom_cost_price);
        const hasCustomCostPrice = isPriceEdited && !isNaN(customCostPriceVal) && customCostPriceVal >= 0;
        const baseCost = hasCustomCostPrice ? customCostPriceVal : (parseFloat(product.pricing?.cost_price) || 0);

        const finalPrice = calculateProductFinalPrice(basePrice, product);

        totalPrice = finalPrice * quantity;
        totalCost = baseCost * quantity;

        avgPricePerUnit = finalPrice;
        avgCostPerUnit = baseCost;
    }

    return {
        quantity,
        final_price_per_unit: avgPricePerUnit,
        final_cost_per_unit: avgCostPerUnit,
        total_price: totalPrice,
        total_cost: totalCost,
    };
};

/**
 * Calculate day totals for itinerary days
 */
export const calculateDayTotals = (day, paxTypes) => {
    let totalPrice = 0;
    let totalCost = 0;

    day.products?.forEach((product) => {
        const productTotals = calculateProductTotals(product, paxTypes);
        totalPrice += productTotals.total_price;
        totalCost += productTotals.total_cost;
    });

    return {
        total_price: totalPrice,
        total_cost: totalCost,
        profit: totalPrice - totalCost,
    };
};

/**
 * Collect per-day extras from itinerary days within a hotel stay's date range
 * For room_night_daily hotels, extras come from hotel.extras directly (with applicable_dates)
 * For legacy hotels, extras come from day.extras in the itinerary
 */
export const getExtrasForStay = (hotel, itinerary) => {
    const isRoomNightDaily = hotel.room_stays?.length > 0;

    // NEW format: extras on hotel object directly
    if (isRoomNightDaily) {
        return hotel.extras || [];
    }

    // LEGACY format: extras from itinerary days within date range
    if (!hotel.check_in_date || !hotel.check_out_date || !itinerary) return [];

    const extras = [];
    itinerary.forEach(day => {
        if (!day.date || !day.extras || day.extras.length === 0) return;
        if (day.date >= hotel.check_in_date && day.date < hotel.check_out_date) {
            day.extras.forEach(extra => {
                extras.push(extra);
            });
        }
    });

    return extras;
};

/**
 * Calculate a single extra's sell/cost amount based on its pricing_basis
 * Formulas: once=×1, per_night=×hotelNights, per_room=×rooms,
 *           room_night=×rooms×hotelNights, per_person=×totalPax
 * For new format, uses applicable_dates.length instead of days_count/hotelNights
 */
export const calculateExtraAmount = (extra, rooms, hotelNights, totalPax) => {
    const sell = parseFloat(extra.custom_sell_price) || 0;
    const cost = parseFloat(extra.custom_cost_price) || 0;
    const qty = parseInt(extra.quantity) || 1;
    const daysCount = parseInt(extra.days_count) || 0;
    const applicableDates = extra.applicable_dates?.length || 0;
    const effectiveDates = applicableDates > 0 ? applicableDates : (daysCount > 0 ? daysCount : hotelNights);

    let sellMultiplier, costMultiplier;

    switch (extra.pricing_basis) {
        case 'once':
            sellMultiplier = qty;
            costMultiplier = qty;
            break;
        case 'per_night':
            sellMultiplier = effectiveDates * qty;
            costMultiplier = sellMultiplier;
            break;
        case 'per_room':
            sellMultiplier = rooms * qty;
            costMultiplier = sellMultiplier;
            break;
        case 'room_night':
            sellMultiplier = effectiveDates * rooms * qty;
            costMultiplier = sellMultiplier;
            break;
        case 'per_person':
            sellMultiplier = totalPax * qty;
            costMultiplier = sellMultiplier;
            break;
        default:
            sellMultiplier = qty;
            costMultiplier = qty;
    }

    return {
        sell_price: sell * sellMultiplier,
        cost_price: cost * costMultiplier,
    };
};

/**
 * Calculate totals for a room_night_daily hotel (room_stays + integrated extras)
 */
export const calculateRoomNightDailyTotals = (hotel, paxTypes) => {
    const totalPax = (paxTypes || []).reduce((sum, pt) => sum + (pt.count || 0), 0);
    const roomStays = hotel.room_stays || [];
    const totalNights = roomStays.length;
    const customRoomPrices = hotel.pricing?.custom_room_prices || [];
    const customExtraPrices = hotel.pricing?.custom_extra_prices || [];

    let totalPrice = 0;
    let totalCost = 0;

    // 1. Calculate room_stays totals (with Quotation overrides)
    roomStays.forEach((rs, i) => {
        const overrideSell = customRoomPrices[i];
        const overrideCost = hotel.pricing?.custom_room_costs?.[i];
        const sellPrice = overrideSell !== undefined ? parseFloat(overrideSell) : (rs.custom_sell_price ?? rs.base_sell_price ?? 0);
        const costPrice = overrideCost !== undefined ? parseFloat(overrideCost) : (rs.custom_cost_price ?? rs.base_cost_price ?? 0);
        const roomsCount = rs.rooms_count || 1;
        totalPrice += parseFloat(sellPrice) * roomsCount;
        totalCost += parseFloat(costPrice) * roomsCount;
    });

    // 2. Calculate integrated extras (with Quotation overrides)
    const extras = hotel.extras || [];
    extras.forEach((extra, i) => {
        const extraOverride = customExtraPrices[i];
        const effectiveSell = extraOverride?.sell_price !== undefined ? parseFloat(extraOverride.sell_price) : (parseFloat(extra.custom_sell_price) || 0);
        const effectiveCost = extraOverride?.cost_price !== undefined ? parseFloat(extraOverride.cost_price) : (parseFloat(extra.custom_cost_price) || 0);
        const extraWithOverride = { ...extra, custom_sell_price: effectiveSell, custom_cost_price: effectiveCost };
        const amounts = calculateExtraAmount(
            extraWithOverride,
            roomStays.reduce((max, rs) => Math.max(max, rs.rooms_count || 1), 0),
            totalNights,
            totalPax
        );
        totalPrice += amounts.sell_price;
        totalCost += amounts.cost_price;
    });

    return {
        total_price: totalPrice,
        total_cost: totalCost,
        profit: totalPrice - totalCost,
        extras_count: extras.length,
    };
};

/**
 * Calculate totals for a single hotel stay (base product + per-day extras)
 * Automatically detects new (room_night_daily) vs legacy (per_person_night) format
 */
export const calculateHotelStayTotals = (hotel, paxTypes, itinerary) => {
    const isRoomNightDaily = hotel.room_stays?.length > 0;

    if (isRoomNightDaily) {
        return calculateRoomNightDailyTotals(hotel, paxTypes);
    }

    // LEGACY format
    const paxTypesArray = Array.isArray(paxTypes) ? paxTypes : [];
    const totalPax = paxTypesArray.reduce((sum, pt) => sum + (pt.count || 0), 0);
    const rooms = hotel.rooms || 1;
    const hotelNights = hotel.nights || 1;
    const customPaxPrices = hotel.pricing?.custom_pax_prices || {};
    const customPaxCosts = hotel.pricing?.custom_pax_costs || {};

    let totalPrice = 0;
    let totalCost = 0;

    // 1. Calculate per_person_night base pricing
    const paxPrices = hotel.product_snapshot?.pax_type_prices || [];
    if (paxPrices.length > 0) {
        paxTypesArray.forEach(pt => {
            const count = pt.count || 0;
            if (count > 0) {
                const paxConfig = paxPrices.find(p => String(p.pax_type_id) === String(pt.pax_type_id));
                const snapshotPrice = paxConfig ? (parseFloat(paxConfig.price) || 0) : 0;
                const snapshotCost = paxConfig ? (parseFloat(paxConfig.cost_price) || 0) : 0;

                const basePrice = customPaxPrices.hasOwnProperty(pt.pax_type_id) ? (parseFloat(customPaxPrices[pt.pax_type_id]) || 0) : snapshotPrice;
                const baseCost = customPaxCosts.hasOwnProperty(pt.pax_type_id) ? (parseFloat(customPaxCosts[pt.pax_type_id]) || 0) : snapshotCost;

                totalPrice += basePrice * count * hotelNights;
                totalCost += baseCost * count * hotelNights;
            }
        });
    } else {
        // Fallback if no pax type prices: use product's base sell/cost
        const basePrice = parseFloat(hotel.product_snapshot?.original_sell_price) || 0;
        totalPrice += basePrice * totalPax * hotelNights;
    }

    // 2. Calculate per-day extras
    const extras = getExtrasForStay(hotel, itinerary);
    extras.forEach(extra => {
        const amounts = calculateExtraAmount(extra, rooms, hotelNights, totalPax);
        totalPrice += amounts.sell_price;
        totalCost += amounts.cost_price;
    });

    return {
        total_price: totalPrice,
        total_cost: totalCost,
        profit: totalPrice - totalCost,
        extras_count: extras.length,
    };
};

/**
 * Apply global discount with target support (sell, cost, both)
 */
export const applyGlobalDiscount = (subtotalPrice, subtotalCost, globalDiscount) => {
    const discountType = globalDiscount?.type || "zero";
    const discountValue = globalDiscount?.value || 0;
    const target = globalDiscount?.target || "sell";

    if (discountType === "zero" || !discountValue || target === "none") {
        return {
            discount_on_selling: 0,
            discount_on_cost: 0,
            final_selling_price: subtotalPrice,
            final_cost_price: subtotalCost,
        };
    }

    let discountOnSelling = 0;
    let discountOnCost = 0;

    const calculateDiscount = (amount) => {
        if (discountType === "amount") {
            return discountValue;
        } else if (discountType === "percent") {
            return amount * discountValue / 100;
        }
        return 0;
    };

    if (target === "sell" || target === "both") {
        discountOnSelling = calculateDiscount(subtotalPrice);
    }

    if (target === "cost" || target === "both") {
        discountOnCost = calculateDiscount(subtotalCost);
    }

    return {
        discount_on_selling: discountOnSelling,
        discount_on_cost: discountOnCost,
        final_selling_price: Math.max(0, subtotalPrice - discountOnSelling),
        final_cost_price: Math.max(0, subtotalCost - discountOnCost),
    };
};

/**
 * Calculate program totals including hotels
 */
export const calculateProgramTotals = (formData) => {
    const paxTypesInfo = formData.pax_types || [];
    const totalPax = paxTypesInfo.reduce((sum, pt) => sum + (pt.count || 0), 0) || 0;

    let subtotalPrice = 0;
    let subtotalCost = 0;

    let breakdown = {
        hotel: { sell: 0, cost: 0 },
        tour: { sell: 0, cost: 0 },
        transfer: { sell: 0, cost: 0 },
        flight: { sell: 0, cost: 0 },
        other: { sell: 0, cost: 0 }
    };

    const processedProgramOnceFlights = new Set();

    // 1. Calculate itinerary product totals
    formData.itinerary?.forEach((day) => {
        day.products?.forEach((product) => {
            const typeGroup = getProductTypeGroup(product);
            const pricingBehavior = product.pricing_behavior || product.product_snapshot?.pricing_behavior;
            const shouldGroupFlight = typeGroup === 'flight' && pricingBehavior !== 'per_occurrence';
            const productId = product.product_snapshot?.product_id || product.product_id || product.id;

            if (shouldGroupFlight && productId) {
                if (processedProgramOnceFlights.has(productId)) {
                    return; // Skip duplicate grouped flights to match UI grouping
                }
                processedProgramOnceFlights.add(productId);
            }

            const productTotals = calculateProductTotals(product, paxTypesInfo);
            subtotalPrice += productTotals.total_price;
            subtotalCost += productTotals.total_cost;

            let type = typeGroup;

            if (breakdown[type]) {
                breakdown[type].sell += productTotals.total_price;
                breakdown[type].cost += productTotals.total_cost;
            } else {
                breakdown.other.sell += productTotals.total_price;
                breakdown.other.cost += productTotals.total_cost;
            }
        });
    });

    // 2. Calculate hotel stay totals
    const hotels = formData.hotels || [];
    hotels.forEach((hotel) => {
        const stayTotals = calculateHotelStayTotals(hotel, paxTypesInfo, formData.itinerary);
        subtotalPrice += stayTotals.total_price;
        subtotalCost += stayTotals.total_cost;
        breakdown.hotel.sell += stayTotals.total_price;
        breakdown.hotel.cost += stayTotals.total_cost;
    });

    const discountResult = applyGlobalDiscount(
        subtotalPrice,
        subtotalCost,
        formData.global_discount
    );

    const finalSellingPrice = discountResult.final_selling_price;
    const finalCostPrice = discountResult.final_cost_price;
    const profit = finalSellingPrice - finalCostPrice;
    const profitMargin = finalSellingPrice > 0 ? (profit / finalSellingPrice) * 100 : 0;

    return {
        total_pax: totalPax,
        subtotal_selling_price: subtotalPrice,
        subtotal_cost_price: subtotalCost,
        subtotal_profit: subtotalPrice - subtotalCost,
        discount_on_selling: discountResult.discount_on_selling,
        discount_on_cost: discountResult.discount_on_cost,
        total_discount: discountResult.discount_on_selling + discountResult.discount_on_cost,
        final_selling_price: finalSellingPrice,
        final_cost_price: finalCostPrice,
        final_profit: profit,
        profit_margin: profitMargin,
        price_per_person: totalPax > 0 ? finalSellingPrice / totalPax : 0,
        cost_per_person: totalPax > 0 ? finalCostPrice / totalPax : 0,
        breakdown,
    };
};

/**
 * Format currency - Remove .00 if not needed
 */
export const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(num)) return '0';

    return num.toLocaleString('en-US', {
        maximumFractionDigits: 2,
    });
};

/**
 * Format number - Remove .00 if not needed
 */
export const formatNumber = (number, decimals = 2) => {
    if (number === null || number === undefined) return '0';
    const num = typeof number === 'string' ? parseFloat(number) : number;

    if (isNaN(num)) return '0';

    return num.toLocaleString('en-US', {
        maximumFractionDigits: decimals,
    });
};

/**
 * Format simple price - Remove .00 if not needed
 */
export const formatPrice = (amount) => {
    if (amount === null || amount === undefined) return '0';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(num)) return '0';

    return num.toLocaleString('en-US', {
        maximumFractionDigits: 2,
    });
};

/**
 * Get product type group (tour, transfer, flight, other)
 * Uses typeCode, category name, or title to determine the best match
 */
export const getProductTypeGroup = (product) => {
    // 1. Check typeCode
    const typeCode = (product.product_snapshot?.productType?.code || product.product_snapshot?.productType ||
        product.product_snapshot?.product_type?.code || product.product_snapshot?.product_type ||
        product.productType?.code || product.productType ||
        product.product_type?.code || product.product_type || '').toString().toLowerCase();

    if (typeCode.includes('flight')) return 'flight';
    if (typeCode.includes('transfer')) return 'transfer';
    if (typeCode.includes('tour')) return 'tour';

    // 2. Check category name
    const cat = product.product_snapshot?.category;
    let catStr = '';
    if (typeof cat === 'string') {
        catStr = cat.toLowerCase();
    } else if (cat) {
        catStr = (cat.name?.en || cat.name || cat.code || '').toString().toLowerCase();
    }

    if (catStr.includes('flight')) return 'flight';
    if (catStr.includes('transfer') || catStr.includes('transport')) return 'transfer';
    if (catStr.includes('tour') || catStr.includes('activity')) return 'tour';

    // 3. Check product title as last resort
    const title = (product.product_snapshot?.title?.en || product.product_snapshot?.title || '').toString().toLowerCase();
    if (title.includes('flight')) return 'flight';
    if (title.includes('transfer') || title.includes('airport')) return 'transfer';
    if (title.includes('tour') || title.includes('trip') || title.includes('safari')) return 'tour';

    return 'other';
};
